<template>
<PageContent>
  <form @submit.prevent="save">
    <NoticeBox type="error" v-if="hasErrors">
      {{ invalidMessage }}
    </NoticeBox>
    <div>
      <!-- 新郎新婦の情報 -->
      <Headline :level="2">会員情報</Headline>
      <div class="form-group">
        <label>会員番号</label>
        <p class="text-paragraph">{{ groomBride.cmc_no | with-empty }}</p>
      </div>
      <div class="form-group">
        <label>お名前(漢字)</label>
        <p class="text-paragraph">{{ groomBride.name | with-empty }}</p>
      </div>
      <div class="form-group">
        <label>お名前(フリガナ)</label>
        <p class="text-paragraph">{{ groomBride.name_kana | with-empty }}</p>
      </div>
      <div class="form-group">
        <label>お名前(ローマ字)</label>
        <div class="form-group">
          <label for="last_name_romaji_old">Last Name</label>
          <div class="vertical-center">
            <input
              id="last_name_romaji_old"
              type="text"
              maxlength="100"
              class="form-control"
              v-model="groomBride.last_name_romaji_old"
            />
          </div>
        </div>
        <div class="form-group">
          <label for="first_name_romaji">First Name</label>
          <div class="vertical-center">
            <input
              id="first_name_romaji"
              type="text"
              maxlength="100"
              class="form-control"
              v-model="groomBride.first_name_romaji"
            />
          </div>
        </div>
        <div class="form-group">
          <label for="last_name_romaji">Married Name（新姓）</label>
          <div class="vertical-center">
            <input
              id="last_name_romaji"
              type="text"
              maxlength="100"
              class="form-control"
              v-model="groomBride.last_name_romaji"
              :disabled="isSelectMarriedNameGroom"
            />
          </div>
        </div>
      </div>
      <div class="form-group">
        <div class="form-group required" v-if="isEmailEdit">
          <label for="email">メールアドレス</label>
          <input
            id="email"
            type="text"
            class="form-control"
            :class="{ 'has-error': hasError('groomBride.mail') }"
            v-model="groomBride.mail"
          />
          <FormError :errors="errors['groomBride.mail'] || []" />
        </div>
        <div v-else>
          <label class="confirm">メールアドレス</label>
          <p class="text-paragraph text-break" v-if="customer.groomBride">{{ customer.groomBride.mail | with-empty }}</p>
        </div>
      </div>
      <div class="form-group required">
        <label for="tel">可能先電話番号</label>
        <div class="vertical-center">
          <input
            type="text"
            class="form-control small digits-3"
            v-model="brideTel.tel_area_no"
            :class="{ 'has-error': hasError('brideTel.tel_area_no') }"
          />
          <span class="space-x-1">-</span>
          <input
            type="text"
            class="form-control small digits-4"
            v-model="brideTel.tel_local_no"
            :class="{ 'has-error': hasError('brideTel.tel_local_no') }"
          />
          <span class="space-x-1">-</span>
          <input
            type="text"
            class="form-control small digits-4"
            v-model="brideTel.tel_no"
            :class="{ 'has-error': hasError('brideTel.tel_no') }"
          />
        </div>
        <FormError :errors="errors['brideTel.tel_area_no'] || []" />
        <FormError :errors="errors['brideTel.tel_local_no'] || []" />
        <FormError :errors="errors['brideTel.tel_no'] || []" />
      </div>
      <div class="form-group required">
        <label >生年月日</label>
        <YearMonthDateSelect
          v-model="groomBride.birth_date"
          :years="birthdayYears"
        />
        <FormError :errors="errors['groomBride.birth_date'] || []" />
      </div>
      <div class="form-group" v-if="groomBride.attribute === '1' && !groomBride.divorce">
        <label >ご結婚記念日</label>
        <YearMonthDateSelect
          v-model="groomBride.wedding_anniversary_date"
          :years="anniversaryYears"
        />
      </div>
      <div class="groomBride_address1">
        <Headline :level="3">住所（現住所）</Headline>
        <div class="form-group required">
          <label>郵便番号</label>
          <div class="vertical-center">
            <span class="space-r-half text-xs">〒</span>
            <input
              type="text"
              class="form-control small digits-3"
              maxlength="3"
              v-model="groomBride.address1_postcode1"
              :class="{ 'has-error': hasError('groomBride.address1_postcode1') }"
            />
            <span class="space-x-1">-</span>
            <input
              type="text"
              class="form-control small digits-4 space-r-1-half"
              maxlength="4"
              v-model="groomBride.address1_postcode2"
              :class="{ 'has-error': hasError('groomBride.address1_postcode2') }"
            />
            <WaveButton
              type="button"
              size="small"
              class="button"
              @click="autoFillAddress('groomBride', 'address1')"
            >住所入力</WaveButton>
          </div>
          <FormError :errors="errors['groomBride.address1_postcode1'] || []" />
          <FormError :errors="errors['groomBride.address1_postcode2'] || []" />
        </div>
        <div class="form-group required">
          <label>都道府県</label>
          <div class="select">
            <select
              class="form-control inline"
              v-model="groomBride.address1_billing_state"
            >
              <option
                v-for="prefecture in prefectures"
                :value="prefecture.name"
                :key="`prefecture-${prefecture.code}`"
              >{{ prefecture.name }}</option>
            </select>
          </div>
          <FormError :errors="errors['groomBride.address1_billing_state'] || []" />
        </div>
        <div class="form-group required">
          <label for="city">市区町村郡</label>
          <input
            id="city"
            type="text"
            class="form-control"
            v-model="groomBride.address1_billing_city"
          />
          <FormError :errors="errors['groomBride.address1_billing_city'] || []" />
        </div>
        <div class="form-group required">
          <label for="town">町名番地</label>
          <input
            id="town"
            type="text"
            class="form-control"
            v-model="groomBride.address1_billing_street"
          />
          <p class="form-memo">例）丸の内2-1-1</p>
          <FormError :errors="errors['groomBride.address1_billing_street'] || []" />
        </div>
        <div class="form-group">
          <label for="building">建物名</label>
          <input
            id="building"
            type="text"
            class="form-control"
            v-model="groomBride.address1_building_name"
          />
          <p class="form-memo">例）◯◯ビル 101号室</p>
        </div>
      </div>
      <div class="groomBride_address1">
        <Headline :level="3">住所（新居）</Headline>
        <div class="form-group">
          <label for="copy_address" class="checkbox">
            <input
              id="copy_address"
              type="checkbox"
              v-model="copyAddress"
              @change="changeAddress"
            />
            <span class="checkbox-text">住所（現住所）と同じ</span>
          </label>
        </div>
        <div class="form-group">
          <label>郵便番号</label>
          <div class="vertical-center">
            <span class="space-r-half text-xs">〒</span>
            <input
              type="text"
              class="form-control small digits-3"
              maxlength="3"
              v-model="groomBride.address2_postcode1"
              :class="{ 'has-error': hasError('groomBride.address2_postcode1') }"
            />
            <span class="space-x-1">-</span>
            <input
              type="text"
              class="form-control small digits-4 space-r-1-half"
              maxlength="4"
              v-model="groomBride.address2_postcode2"
              :class="{ 'has-error': hasError('groomBride.address2_postcode2') }"
            />
            <WaveButton
              type="button"
              size="small"
              class="button"
              @click="autoFillAddress('groomBride', 'address2')"
            >住所入力</WaveButton>
          </div>
          <FormError :errors="errors['groomBride.address2_postcode1'] || []" />
          <FormError :errors="errors['groomBride.address2_postcode2'] || []" />
        </div>
        <div class="form-group">
          <label>都道府県</label>
          <div class="select">
            <select
              class="form-control inline"
              v-model="groomBride.address2_billing_state"
            >
              <option
                v-for="prefecture in prefectures"
                :value="prefecture.name"
                :key="`prefecture-${prefecture.code}`"
              >{{ prefecture.name }}</option>
            </select>
          </div>
        </div>
        <div class="form-group">
          <label for="city">市区町村郡</label>
          <input
            id="city"
            type="text"
            class="form-control"
            v-model="groomBride.address2_billing_city"
          />
        </div>
        <div class="form-group">
          <label for="town">町名番地</label>
          <input
            id="town"
            type="text"
            class="form-control"
            v-model="groomBride.address2_billing_street"
          />
          <p class="form-memo">例）丸の内2-1-1</p>
        </div>
        <div class="form-group">
          <label for="building">建物名</label>
          <input
            id="building"
            type="text"
            class="form-control"
            v-model="groomBride.address2_building_name"
          />
          <p class="form-memo">例）◯◯ビル 101号室</p>
        </div>
      </div>
      <Headline :level="3">その他</Headline>
      <div class="form-group">
        <label for="business_place">勤務先(会社名)</label>
        <input
          id="business_place"
          type="text"
          class="form-control"
          v-model="input.groomBride.business_place"
        />
      </div>
      <div class="form-group">
        <label for="unit">勤務先(部署)</label>
        <input
          id="unit"
          type="text"
          class="form-control"
          v-model="input.groomBride.unit"
        />
      </div>
      <div class="form-group" v-if="!onlyLastName && !isGroom">
        <label>新姓</label>
        <div class="select">
          <select
            class="form-control inline"
            v-model="selectMarriedName"
            @change="onSelectMarriedNameChange"
          >
            <option
              v-for="marriedName in marriedNames"
              :value="marriedName.code"
              :key="`marriedName-${marriedName.code}`"
            >{{ marriedName.name }}</option>
          </select>
        </div>
        <p class="text-light text-xxxs-m text-xs-t space-b-2">
          ※新婦姓の際にはプランナーへお声がけください。
        </p>
      </div>
      <ChildInfo v-if="childInfoFlag"
        :edit="true"
        :child-info="childInfo"
        :update="updateChild"
        :child-info-flag="childInfoFlag"
        :errors="errors"
      />
      <!-- 親御様の情報 -->
      <Headline :level="2">親御様情報</Headline>
      <Headline :level="3">父親</Headline>
      <div class="form-group">
        <label>お名前(漢字)</label>
        <div class="form-group">
          <label for="last_name">姓</label>
          <div class="vertical-center">
            <input
              id="last_name"
              type="text"
              maxlength="100"
              class="form-control"
              v-model="father.last_name"
            />
          </div>
        </div>
        <div class="form-group">
          <label for="first_name">名</label>
          <div class="vertical-center">
            <input
              id="first_name"
              type="text"
              maxlength="100"
              class="form-control"
              v-model="father.first_name"
            />
          </div>
        </div>
      </div>
      <div class="form-group">
        <label>お名前(フリガナ)</label>
        <div class="form-group">
          <label for="last_name_kana">セイ</label>
          <div class="vertical-center">
            <input
              id="last_name_kana"
              type="text"
              maxlength="100"
              class="form-control"
              v-model="father.last_name_kana"
            />
          </div>
        </div>
        <div class="form-group">
          <label for="first_name_kana">メイ</label>
          <div class="vertical-center">
            <input
              id="first_name_kana"
              type="text"
              maxlength="100"
              class="form-control"
              v-model="father.first_name_kana"
            />
          </div>
        </div>
      </div>
      <div class="form-group" v-if="false">
        <label>お名前(ローマ字)</label>
        <div class="form-group">
          <label for="last_name_romaji">Last Name</label>
          <div class="vertical-center">
            <input
              id="last_name_romaji"
              type="text"
              maxlength="100"
              class="form-control"
              v-model="father.last_name_romaji"
            />
          </div>
        </div>
        <div class="form-group">
          <label for="first_name_romaji">First Name</label>
          <div class="vertical-center">
            <input
              id="first_name_romaji"
              type="text"
              maxlength="100"
              class="form-control"
              v-model="father.first_name_romaji"
            />
          </div>
        </div>
      </div>
      <div class="form-group">
        <label >生年月日</label>
        <YearMonthDateSelect
          v-model="father.birth_date"
          :years="birthdayYears"
        />
      </div>
      <div class="form-group">
        <label for="tel">電話番号</label>
        <div class="vertical-center">
          <input
            type="text"
            class="form-control small digits-3"
            v-model="father.tel1_area_no"
            :class="{ 'has-error': hasError('father.tel1_area_no') }"
          />
          <span class="space-x-1">-</span>
          <input
            type="text"
            class="form-control small digits-4"
            v-model="father.tel1_local_no"
            :class="{ 'has-error': hasError('father.tel1_local_no') }"
          />
          <span class="space-x-1">-</span>
          <input
            type="text"
            class="form-control small digits-4"
            v-model="father.tel1_no"
            :class="{ 'has-error': hasError('father.tel1_no') }"
          />
        </div>
        <FormError :errors="errors['father.tel1_area_no'] || []" />
        <FormError :errors="errors['father.tel1_local_no'] || []" />
        <FormError :errors="errors['father.tel1_no'] || []" />
      </div>
      <div class="form-group">
        <div class="vertical-center" style="top-margin: 10px;">
          <input
            type="text"
            class="form-control small digits-3"
            v-model="father.tel2_area_no"
            :class="{ 'has-error': hasError('father.tel2_area_no') }"
          />
          <span class="space-x-1">-</span>
          <input
            type="text"
            class="form-control small digits-4"
            v-model="father.tel2_local_no"
            :class="{ 'has-error': hasError('father.tel2_local_no') }"
          />
          <span class="space-x-1">-</span>
          <input
            type="text"
            class="form-control small digits-4"
            v-model="father.tel2_no"
            :class="{ 'has-error': hasError('father.tel2_no') }"
          />
        </div>
        <FormError :errors="errors['father.tel2_area_no'] || []" />
        <FormError :errors="errors['father.tel2_local_no'] || []" />
        <FormError :errors="errors['father.tel2_no'] || []" />
      </div>
      <div class="form-group">
        <div class="vertical-center" style="top-margin: 10px;">
          <input
            type="text"
            class="form-control small digits-3"
            v-model="father.tel3_area_no"
            :class="{ 'has-error': hasError('father.tel3_area_no') }"
          />
          <span class="space-x-1">-</span>
          <input
            type="text"
            class="form-control small digits-4"
            v-model="father.tel3_local_no"
            :class="{ 'has-error': hasError('father.tel3_local_no') }"
          />
          <span class="space-x-1">-</span>
          <input
            type="text"
            class="form-control small digits-4"
            v-model="father.tel3_no"
            :class="{ 'has-error': hasError('father.tel3_no') }"
          />
        </div>
        <FormError :errors="errors['father.tel3_area_no'] || []" />
        <FormError :errors="errors['father.tel3_local_no'] || []" />
        <FormError :errors="errors['father.tel3_no'] || []" />
      </div>
      <Headline :level="3">母親</Headline>
      <div class="form-group">
        <label>お名前(漢字)</label>
        <div class="form-group">
          <label for="last_name">姓</label>
          <div class="vertical-center">
            <input
              id="last_name"
              type="text"
              maxlength="100"
              class="form-control"
              v-model="mother.last_name"
            />
          </div>
        </div>
        <div class="form-group">
          <label for="first_name">名</label>
          <div class="vertical-center">
            <input
              id="first_name"
              type="text"
              maxlength="100"
              class="form-control"
              v-model="mother.first_name"
            />
          </div>
        </div>
      </div>
      <div class="form-group">
        <label>お名前(フリガナ)</label>
        <div class="form-group">
          <label for="last_name_kana">セイ</label>
          <div class="vertical-center">
            <input
              id="last_name_kana"
              type="text"
              maxlength="100"
              class="form-control"
              v-model="mother.last_name_kana"
            />
          </div>
        </div>
        <div class="form-group">
          <label for="first_name_kana">メイ</label>
          <div class="vertical-center">
            <input
              id="first_name_kana"
              type="text"
              maxlength="100"
              class="form-control"
              v-model="mother.first_name_kana"
            />
          </div>
        </div>
      </div>
      <div class="form-group" v-if="false">
        <label>お名前(ローマ字)</label>
        <div class="form-group">
          <label for="last_name_romaji">Last Name</label>
          <div class="vertical-center">
            <input
              id="last_name_romaji"
              type="text"
              maxlength="100"
              class="form-control"
              v-model="mother.last_name_romaji"
            />
          </div>
        </div>
        <div class="form-group">
          <label for="first_name_romaji">First Name</label>
          <div class="vertical-center">
            <input
              id="first_name_romaji"
              type="text"
              maxlength="100"
              class="form-control"
              v-model="mother.first_name_romaji"
            />
          </div>
        </div>
      </div>
      <div class="form-group">
        <label >生年月日</label>
        <YearMonthDateSelect
          v-model="mother.birth_date"
          :years="birthdayYears"
        />
      </div>
      <div class="form-group">
        <label for="tel">電話番号</label>
        <div class="vertical-center">
          <input
            type="text"
            class="form-control small digits-3"
            v-model="mother.tel1_area_no"
            :class="{ 'has-error': hasError('mother.tel1_area_no') }"
          />
          <span class="space-x-1">-</span>
          <input
            type="text"
            class="form-control small digits-4"
            v-model="mother.tel1_local_no"
            :class="{ 'has-error': hasError('mother.tel1_local_no') }"
          />
          <span class="space-x-1">-</span>
          <input
            type="text"
            class="form-control small digits-4"
            v-model="mother.tel1_no"
            :class="{ 'has-error': hasError('mother.tel1_no') }"
          />
        </div>
        <FormError :errors="errors['mother.tel1_area_no'] || []" />
        <FormError :errors="errors['mother.tel1_local_no'] || []" />
        <FormError :errors="errors['mother.tel1_no'] || []" />
      </div>
      <div class="form-group">
        <div class="vertical-center" style="top-margin: 10px;">
          <input
            type="text"
            class="form-control small digits-3"
            v-model="mother.tel2_area_no"
            :class="{ 'has-error': hasError('mother.tel2_area_no') }"
          />
          <span class="space-x-1">-</span>
          <input
            type="text"
            class="form-control small digits-4"
            v-model="mother.tel2_local_no"
            :class="{ 'has-error': hasError('mother.tel2_local_no') }"
          />
          <span class="space-x-1">-</span>
          <input
            type="text"
            class="form-control small digits-4"
            v-model="mother.tel2_no"
            :class="{ 'has-error': hasError('mother.tel2_no') }"
          />
        </div>
        <FormError :errors="errors['mother.tel2_area_no'] || []" />
        <FormError :errors="errors['mother.tel2_local_no'] || []" />
        <FormError :errors="errors['mother.tel2_no'] || []" />
      </div>
      <div class="form-group">
        <div class="vertical-center" style="top-margin: 10px;">
          <input
            type="text"
            class="form-control small digits-3"
            v-model="mother.tel3_area_no"
            :class="{ 'has-error': hasError('mother.tel3_area_no') }"
          />
          <span class="space-x-1">-</span>
          <input
            type="text"
            class="form-control small digits-4"
            v-model="mother.tel3_local_no"
            :class="{ 'has-error': hasError('mother.tel3_local_no') }"
          />
          <span class="space-x-1">-</span>
          <input
            type="text"
            class="form-control small digits-4"
            v-model="mother.tel3_no"
            :class="{ 'has-error': hasError('mother.tel3_no') }"
          />
        </div>
        <FormError :errors="errors['mother.tel3_area_no'] || []" />
        <FormError :errors="errors['mother.tel3_local_no'] || []" />
        <FormError :errors="errors['mother.tel3_no'] || []" />
      </div>
      <div class="parent_address1">
        <label>住所</label>
        <div class="form-group">
          <label>郵便番号</label>
          <div class="vertical-center">
            <span class="space-r-half text-xs">〒</span>
            <input
              type="text"
              class="form-control small digits-3"
              maxlength="3"
              v-model="parentAddress.address1_postcode1"
              :class="{ 'has-error': hasError('parentAddress.address1_postcode1') }"
            />
            <span class="space-x-1">-</span>
            <input
              type="text"
              class="form-control small digits-4 space-r-1-half"
              maxlength="4"
              v-model="parentAddress.address1_postcode2"
              :class="{ 'has-error': hasError('parentAddress.address1_postcode2') }"
            />
            <WaveButton
              type="button"
              size="small"
              class="button"
              @click="autoFillAddress('parentAddress', 'address1')"
            >住所入力</WaveButton>
          </div>
          <FormError :errors="errors['parentAddress.address1_postcode1'] || []" />
          <FormError :errors="errors['parentAddress.address1_postcode2'] || []" />
        </div>
        <div class="form-group">
          <label>都道府県</label>
          <div class="select">
            <select
              class="form-control inline"
              v-model="parentAddress.address1_billing_state"
            >
              <option
                v-for="prefecture in prefectures"
                :value="prefecture.name"
                :key="`prefecture-${prefecture.code}`"
              >{{ prefecture.name }}</option>
            </select>
          </div>
        </div>
        <div class="form-group">
          <label for="city">市区町村郡</label>
          <input
            id="city"
            type="text"
            class="form-control"
            v-model="parentAddress.address1_billing_city"
          />
        </div>
        <div class="form-group">
          <label for="town">町名番地</label>
          <input
            id="town"
            type="text"
            class="form-control"
            v-model="parentAddress.address1_billing_street"
          />
          <p class="form-memo">例）丸の内2-1-1</p>
        </div>
        <div class="form-group">
          <label for="building">建物名</label>
          <input
            id="building"
            type="text"
            class="form-control"
            v-model="parentAddress.address1_building_name"
          />
          <p class="form-memo">例）◯◯ビル 101号室</p>
        </div>
      </div>


      <template v-if="groomBride.shipments.length > 0">
        <Headline :level="2">配信メール情報</Headline>
        <p class="text-light text-xxxs-m text-xs-t space-b-2">
          お受け取りになりたい情報にチェックをお願いいたします
        </p>
        <ul class="check-list">
          <li
            class="check-list-item"
            v-for="(shipment, sIndex) in groomBride.shipments"
            :key="`shipment-${sIndex}`"
          >
            <label :for="`shipment-${sIndex}`" class="checkbox">
              <input
                :id="`shipment-${sIndex}`"
                type="checkbox"
                v-model="shipment.value"
              />
              <span class="checkbox-text">{{ shipment.name }}</span>
            </label>
          </li>
        </ul>
      </template>

      <div class="form-footer">
        <div class="text-center">
          <WaveButton type="submit" class="button" :loading="loading">
            変更内容を保存する
          </WaveButton>
        </div>
        <div class="text-center space-t-3">
          <a href="#" @click.prevent="cancel">前の画面へ戻る</a>
        </div>
      </div>
    </div>
  </form>

  <Modal id="check-email">
    <template #default>
      メールアドレスを変更すると、自動的にログアウトし、再ログインが必要になります。このまま変更してよろしいですか。
    </template>
    <template #footer>
      <WaveButton
        type="button"
        size="small"
        :text="true"
        @click="$modal.close('check-email')"
      >キャンセル</WaveButton>
      <WaveButton
        type="button"
        size="small"
        :text="true"
        @click="updateEmail"
      >OK</WaveButton>
    </template>
  </Modal>
</PageContent>
</template>

<script>
import NoticeBox from '@/components/NoticeBox.vue'
import FormError from '@/components/FormError.vue'
import validationMixin from '@/mixins/validation'
import YearMonthDateSelect from '@/components/YearMonthDateSelect.vue'
import withEmpty from '@/filters/with-empty'
import { formatDate } from '@/filters/format-date'
import ChildInfo from '@/components/ChildInfo'
import zipApi from '@/api/zip'
import { renderError, RESPONSE_EMAIL_EXISTS } from '@/utils/error'
import {
  isValidZipcode,
  getBirthdayYears,
  getAnniversaryYears } from '@/utils/form'
import { EMAIL_EXISTS } from '@/messages/errors'
import Modal from '@/components/Modal.vue'
import customerApi from '@/api/customer'

// お客様情報編集 確認画面
export default {
  mixins: [validationMixin],
  rules: {
    'groomBride.birth_date': ['生年月日', [
      ['required'],
    ]],
    'brideTel.tel_area_no': ['市外局番', [
      ['required'],
      ['pattern', '^(0|\\+)[0-9]{1,9}$', '$name$は0または+から始まる2〜10桁の数値で入力してください。']
    ]],
    'brideTel.tel_local_no': ['市内局番', [
      ['required'],
      ['pattern', '^[0-9]{1,10}$', '$name$は1〜10桁の数値で入力してください。']
    ]],
    'brideTel.tel_no': ['加入者番号', [
      ['required'],
      ['pattern', '^[0-9]{4,10}$', '$name$は4〜10桁の数値で入力してください。']
    ]],
    'groomBride.address1_postcode1': ['郵便番号（前半）', [
      ['required'],
      ['pattern', '^[0-9]{3}$', '$name$は3桁の数値で入力してください。']
    ]],
    'groomBride.address1_postcode2': ['郵便番号（後半）', [
      ['required'],
      ['pattern', '^[0-9]{4}$', '$name$は4桁の数値で入力してください。']
    ]],
    'groomBride.address1_billing_state': ['都道府県', [
      ['required'],
    ]],
    'groomBride.address1_billing_city': ['市区町村郡', [
      ['required'],
    ]],
    'groomBride.address1_billing_street': ['市区町村郡', [
      ['required'],
    ]],
    'groomBride.address2_postcode1': ['郵便番号（前半）', [
      ['pattern', '^[0-9]{3}$', '$name$は3桁の数値で入力してください。']
    ]],
    'groomBride.address2_postcode2': ['郵便番号（後半）', [
      ['pattern', '^[0-9]{4}$', '$name$は4桁の数値で入力してください。']
    ]],
    'account.child': ['お子様情報', [
      ['child', '', 'お子様情報は全て選択または未選択としてください。']
    ]],
    // 親御様のバリデーション
    'father.tel1_area_no': ['市外局番', [
      ['pattern', '^(0|\\+)[0-9]{1,9}$', '$name$は0または+から始まる2〜10桁の数値で入力してください。']
    ]],
    'father.tel1_local_no': ['市内局番', [
      ['pattern', '^[0-9]{1,10}$', '$name$は1〜10桁の数値で入力してください。']
    ]],
    'father.tel1_no': ['加入者番号', [
      ['pattern', '^[0-9]{4,10}$', '$name$は4〜10桁の数値で入力してください。']
    ]],
    'father.tel2_area_no': ['市外局番', [
      ['pattern', '^(0|\\+)[0-9]{1,9}$', '$name$は0または+から始まる2〜10桁の数値で入力してください。']
    ]],
    'father.tel2_local_no': ['市内局番', [
      ['pattern', '^[0-9]{1,10}$', '$name$は1〜10桁の数値で入力してください。']
    ]],
    'father.tel2_no': ['加入者番号', [
      ['pattern', '^[0-9]{4,10}$', '$name$は4〜10桁の数値で入力してください。']
    ]],
    'father.tel3_area_no': ['市外局番', [
      ['pattern', '^(0|\\+)[0-9]{1,9}$', '$name$は0または+から始まる2〜10桁の数値で入力してください。']
    ]],
    'father.tel3_local_no': ['市内局番', [
      ['pattern', '^[0-9]{1,10}$', '$name$は1〜10桁の数値で入力してください。']
    ]],
    'father.tel3_no': ['加入者番号', [
      ['pattern', '^[0-9]{4,10}$', '$name$は4〜10桁の数値で入力してください。']
    ]],
    'mother.tel1_area_no': ['市外局番', [
      ['pattern', '^(0|\\+)[0-9]{1,9}$', '$name$は0または+から始まる2〜10桁の数値で入力してください。']
    ]],
    'mother.tel1_local_no': ['市内局番', [
      ['pattern', '^[0-9]{1,10}$', '$name$は1〜10桁の数値で入力してください。']
    ]],
    'mother.tel1_no': ['加入者番号', [
      ['pattern', '^[0-9]{4,10}$', '$name$は4〜10桁の数値で入力してください。']
    ]],
    'mother.tel2_area_no': ['市外局番', [
      ['pattern', '^(0|\\+)[0-9]{1,9}$', '$name$は0または+から始まる2〜10桁の数値で入力してください。']
    ]],
    'mother.tel2_local_no': ['市内局番', [
      ['pattern', '^[0-9]{1,10}$', '$name$は1〜10桁の数値で入力してください。']
    ]],
    'mother.tel2_no': ['加入者番号', [
      ['pattern', '^[0-9]{4,10}$', '$name$は4〜10桁の数値で入力してください。']
    ]],
    'mother.tel3_area_no': ['市外局番', [
      ['pattern', '^(0|\\+)[0-9]{1,9}$', '$name$は0または+から始まる2〜10桁の数値で入力してください。']
    ]],
    'mother.tel3_local_no': ['市内局番', [
      ['pattern', '^[0-9]{1,10}$', '$name$は1〜10桁の数値で入力してください。']
    ]],
    'mother.tel3_no': ['加入者番号', [
      ['pattern', '^[0-9]{4,10}$', '$name$は4〜10桁の数値で入力してください。']
    ]],
    'parentAddress.address1_postcode1': ['郵便番号（前半）', [
      ['pattern', '^[0-9]{3}$', '$name$は3桁の数値で入力してください。']
    ]],
    'parentAddress.address1_postcode2': ['郵便番号（後半）', [
      ['pattern', '^[0-9]{4}$', '$name$は4桁の数値で入力してください。']
    ]],
  },
  props: {
    customer: {
      type: Object,
      required: true
    },
    childInfoFlag: {
      type: Boolean,
      required: true
    },
    editCancel: {
      type: Function,
      required: false
    },
    complete: {
      type: Function,
      required: false
    }
  },
  components: {
    NoticeBox,
    FormError,
    YearMonthDateSelect,
    Modal,
    ChildInfo,
  },
  data() {
    return {
      loading: false,
      selectMarriedName: 0,
      marriedNames: [
          {'code': 0, 'name': '-'},
          {'code': 1, 'name': '新郎姓'},
      ],
      input: {},
      childInfo: [],
      groomBride: {},
      father: {},
      mother: {},
      brideTel: {},
      parentAddress: {},
      copyAddress: false,
      account: {},
    }
  },
  computed: {
    accountId() {
      return this.$store.getters['auth/userId']
    },
    onlyLastName() {
      if (
        (
          this.input.groom.last_name.length > 0 && this.input.groom.first_name.length === 0 && this.input.groom.middle_name.length === 0 && this.input.groom.last_name_kanji_old.length === 0
          && this.input.groom.last_name_kana.length === 0 && this.input.groom.first_name_kana.length === 0 && this.input.groom.middle_name_kana.length === 0 && this.input.groom.last_name_kana_old.length === 0
          && this.input.groom.last_name_romaji.length === 0 && this.input.groom.first_name_romaji.length === 0 && this.input.groom.middle_name_romaji.length === 0 && this.input.groom.last_name_romaji_old.length === 0
        )
        ||
        (
          this.input.bride.last_name.length > 0 && this.input.bride.first_name.length === 0 && this.input.bride.middle_name.length === 0 && this.input.bride.last_name_kanji_old.length === 0
          && this.input.bride.last_name_kana.length === 0 && this.input.bride.first_name_kana.length === 0 && this.input.bride.middle_name_kana.length === 0 && this.input.bride.last_name_kana_old.length === 0
          && this.input.bride.last_name_romaji.length === 0 && this.input.bride.first_name_romaji.length === 0 && this.input.bride.middle_name_romaji.length === 0 && this.input.bride.last_name_romaji_old.length === 0
        )
      ) {
        return true;
      }
      return false;
    },
    isEmailEdit() {
      return Number(this.groomBride.customer_id) === Number(this.accountId)
    },
    isGroom() {
      return Number(this.groomBride.attribute) === 1
    },
    isSelectMarriedNameGroom() {
      return (this.selectMarriedName === 1)
    }
  },
  filters: {
    withEmpty,
    formatDate
  },
  methods: {
    initOptions() {
      this.prefectures = require('../data/prefectures.json')
      this.birthdayYears = getBirthdayYears()
      this.anniversaryYears = getAnniversaryYears()

      // 表示情報と入力情報を分けて保持する
      this.input = JSON.parse(JSON.stringify(this.customer))
      this.groomBride = this.input.groomBride
      this.father = this.input.father
      this.mother = this.input.mother

      // お子様情報を整形
      for (let child = 1; child <= 5; child++) {
        this.childInfo.push({
          'sex': this.groomBride.child_info[`child${child}_sex`],
          'year': this.groomBride.child_info[`child${child}_year`],
          'month': this.groomBride.child_info[`child${child}_month`],
        })
      }
      this.account = this.groomBride

      // 入力対象の電話番号を保持
      this.brideTel.tel_area_no = this.customer.groomBride[`tel${this.customer.groomBride.telPriorityNo}_area_no`]
      this.brideTel.tel_local_no = this.customer.groomBride[`tel${this.customer.groomBride.telPriorityNo}_local_no`]
      this.brideTel.tel_no = this.customer.groomBride[`tel${this.customer.groomBride.telPriorityNo}_no`]

      // 親御様の住所(新郎父を使用)
      this.parentAddress = this.customer.parentAddress

      // メールアドレスが編集可能であればバリデーションルールを追加
      if (this.isEmailEdit) {
        this.$options.rules['groomBride.mail'] = ['メールアドレス', ['required', 'email']]
      }
    },
    async autoFillAddress(customerKeyName, addressKeyName) {
      const {
        [`${addressKeyName}_postcode1`]: z1,
        [`${addressKeyName}_postcode2`]: z2 } = this[`${customerKeyName}`]

      if (!isValidZipcode(z1, z2)) {
        return false
      }

      await zipApi.search(z1, z2, ({ pref, city, town, building }) => {
        this[`${customerKeyName}`][`${addressKeyName}_billing_state`] = pref
        this[`${customerKeyName}`][`${addressKeyName}_billing_city`] = city
        this[`${customerKeyName}`][`${addressKeyName}_billing_street`] = town
        this[`${customerKeyName}`][`${addressKeyName}_building_name`] = building
      })
    },
    onSelectMarriedNameChange() {
      if (this.selectMarriedName === 1) {
        this.input.groomBride.last_name_romaji = this.input.groom.last_name_romaji
      } else {
        this.input.groomBride.last_name_romaji = this.input.bride.last_name_romaji
      }
    },
    save() {
      if (!this.validate()) {
        window.scrollTo(0, 0)
        return false
      }

      // 電話番号を入力情報に反映する
      this.groomBride[`tel${this.customer.groomBride.telPriorityNo}_area_no`] = this.brideTel.tel_area_no
      this.groomBride[`tel${this.customer.groomBride.telPriorityNo}_local_no`] = this.brideTel.tel_local_no
      this.groomBride[`tel${this.customer.groomBride.telPriorityNo}_no`] = this.brideTel.tel_no

      // 新姓を反映
      if (!this.onlyLastName) {
        if (parseInt(this.groomBride.attribute) === 1) {
          // 新郎側を編集中
          if (this.selectMarriedName === 1) {
              // 新郎の新姓の場合
              this.input.bride.last_name = this.input.groomBride.last_name
              this.input.bride.last_name_kana = this.input.groomBride.last_name_kana
              this.input.bride.last_name_romaji = this.input.groomBride.last_name_romaji
            } else if (this.selectMarriedName === 2) {
              // 新婦の新姓の場合
              this.input.groomBride.last_name = this.input.bride.last_name
              this.input.groomBride.last_name_kana = this.input.bride.last_name_kana
              this.input.groomBride.last_name_romaji = this.input.bride.last_name_romaji
            }
        } else if (parseInt(this.groomBride.attribute) === 2) {
          // 新婦側を編集中
          if (this.selectMarriedName === 1) {
              // 新郎の新姓の場合
              this.input.groomBride.last_name = this.input.groom.last_name
              this.input.groomBride.last_name_kana = this.input.groom.last_name_kana
              this.input.groomBride.last_name_romaji = this.input.groom.last_name_romaji
            } else if (this.selectMarriedName === 2) {
              // 新婦の新姓の場合
              this.input.groom.last_name = this.input.groomBride.last_name
              this.input.groom.last_name_kana = this.input.groomBride.last_name_kana
              this.input.groom.last_name_romaji = this.input.groomBride.last_name_romaji
            }
        }
      }

      // 親御様住所を反映
      this.father.address1_postcode1 = this.parentAddress.address1_postcode1
      this.father.address1_postcode2 = this.parentAddress.address1_postcode2
      this.father.address1_billing_state = this.parentAddress.address1_billing_state
      this.father.address1_billing_city = this.parentAddress.address1_billing_city
      this.father.address1_billing_street = this.parentAddress.address1_billing_street
      this.father.address1_building_name = this.parentAddress.address1_building_name

      // 親御様母に住所を反映(親御様父と同じ住所を入れる)
      this.mother.address1_postcode1 = this.father.address1_postcode1
      this.mother.address1_postcode2 = this.father.address1_postcode2
      this.mother.address1_billing_state = this.father.address1_billing_state
      this.mother.address1_billing_city = this.father.address1_billing_city
      this.mother.address1_billing_street = this.father.address1_billing_street
      this.mother.address1_building_name = this.father.address1_building_name
      this.mother.address1_postcode = this.father.address1_postcode
      this.mother.address1 = this.father.address1

      // 顧客情報の加工
      let customerKeys = ['groomBride', 'father', 'mother']
      customerKeys.forEach((value) => {
        // 顧客名
        this[value].name = this[value].last_name.concat(this[value].middle_name, this[value].first_name)
        this[value].name_old = this[value].last_name_kanji_old.concat(this[value].middle_name, this[value].first_name)
        this[value].name_kana = this[value].last_name_kana.concat(this[value].middle_name_kana, this[value].first_name_kana)
        this[value].name_kana_old = this[value].last_name_kana_old.concat(this[value].middle_name_kana, this[value].first_name_kana)
        this[value].name_romaji = this[value].last_name_romaji.concat(this[value].middle_name_romaji, this[value].first_name_romaji)
        this[value].name_romaji_old = this[value].last_name_romaji_old.concat(this[value].middle_name_romaji, this[value].first_name_romaji)
        this[value].name_concat = this[value].name.concat(
          this[value].name_kana,
          this[value].name_romaji,
          this[value].name_old,
          this[value].name_kana_old,
          this[value].name_romaji_old
        )

        // 住所
        this[value].address1_postcode = this[value].address1_postcode1.concat(this[value].address1_postcode2)
        this[value].address1 = this[value].address1_postcode1.concat(
          this[value].address1_postcode2,
          this[value].address1_billing_state,
          this[value].address1_billing_city,
          this[value].address1_billing_street,
          this[value].address1_building_name,
        )
        this[value].address2_postcode = this[value].address2_postcode1.concat(this[value].address2_postcode2)
        this[value].address2 = this[value].address2_postcode1.concat(
          this[value].address2_postcode2,
          this[value].address2_billing_state,
          this[value].address2_billing_city,
          this[value].address2_billing_street,
          this[value].address2_building_name,
        )
      });

      // 入力情報を反映
      if (parseInt(this.groomBride.attribute) === 1) {
        this.input.groom = this.groomBride
        this.input.groom_father = this.father
        this.input.groom_mother = this.mother
      } else if (parseInt(this.groomBride.attribute) === 2) {
        this.input.bride = this.groomBride
        this.input.bride_father = this.father
        this.input.bride_mother = this.mother
      }

      if (this.customer.groomBride.mail === this.groomBride.mail) {
        // メールアドレス変更なし
        return this.submit()
      }

      this.$modal.show('check-email')
    },
    updateEmail() {
      this.$modal.close('check-email')
      this.submit(() => this.$wcRouter.push({ name: 'logout' }))
    },
    async submit(onSuccess) {
      if (this.loading) {
        return false
      }

      this.loading = true

      try {
        // 新郎情報の編集時は新郎情報のみ新婦情報の編集時は新婦情報のみセット
        let registInfo = {}
        if (parseInt(this.groomBride.attribute) === 1) {
          registInfo = {
            'groom': this.input.groom,
            'bride': '',
            'groom_father': this.input.groom_father,
            'groom_mother': this.input.groom_mother,
            'bride_father': '',
            'bride_mother': ''
          }
        } else {
          registInfo = {
            'groom': '',
            'bride': this.input.bride,
            'groom_father': '',
            'groom_mother': '',
            'bride_father': this.input.bride_father,
            'bride_mother': this.input.bride_mother
          }
        }

        await customerApi.update(
          this.accountId, registInfo
        )

        if (typeof onSuccess === 'function') {
          onSuccess()
          return false
        }

        this.loading = false
        this.complete()
      } catch(err) {
        this.loading = false

        // メールアドレスがすでに使用されていたとき
        if (err.response && err.response.status === RESPONSE_EMAIL_EXISTS) {
          this.addValidateError('groomBride.mail', EMAIL_EXISTS)
          return window.scrollTo(0, 0)
        }

        renderError(err)
      }
    },
    updateChild(info) {
      this.childInfo[info.idx].sex = info.sex
      this.childInfo[info.idx].year = info.year
      this.childInfo[info.idx].month = info.month

      this.account.child_info[`${info.key}_sex`] = this.childInfo[info.idx].sex
      this.account.child_info[`${info.key}_year`] = this.childInfo[info.idx].year
      this.account.child_info[`${info.key}_month`] = this.childInfo[info.idx].month
    },
    changeAddress() {
      if (this.copyAddress) {
        // 現住所を新居にコピー
        this.groomBride.address2_postcode1 = this.groomBride.address1_postcode1
        this.groomBride.address2_postcode2 = this.groomBride.address1_postcode2
        this.groomBride.address2_billing_state = this.groomBride.address1_billing_state
        this.groomBride.address2_billing_city = this.groomBride.address1_billing_city
        this.groomBride.address2_billing_street = this.groomBride.address1_billing_street
        this.groomBride.address2_building_name = this.groomBride.address1_building_name
      }
    },
    cancel() {
      if (this.loading) {
        return false
      }
      this.editCancel()
      window.scrollTo(0, 0)
    }
  },
  created() {
    this.$withLoading(() => Promise.all([
      this.initOptions()
    ]))
  }
}
</script>
